import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import { merge } from 'lodash';
import { useTranslation } from 'react-i18next';

import WDT_STUDENT_PROGRESS_QUERY from 'graph/reports/wdt-student-progress-report-query';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import BarChart from 'components/@common/Charts/BarChart';
import CharacterOrderAndFilter from '../../common/CharacterOrderAndFilter';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { ModuleType } from '../../../../types';
import { getQueryVariables } from '../../../../utils/report-param-utils';
import { PrintViewContext } from '../../../../providers/PrintViewProvider';
import { MODULE_COLOR_MAP, BAR_CHART_COMMON_OPTIONS } from '../../../../utils/report-wdt-utils';
import {
  WdtCharacterGroup,
  WdtCharacterOrder,
  WdtCharacterProgressData
} from 'types/schema-generated-types';

interface ProgressForCharactersChartViewProps {
  module: ModuleType;
}

const ProgressForCharactersChartView: React.FC<ProgressForCharactersChartViewProps> = ({
  module
}) => {
  const { fullChartWidth } = useContext(PrintViewContext);
  const [{ dateRange, entityType, entityId }] = useReportQueryParams();
  const [fetchReport, { data, loading }] = useLazyQuery(WDT_STUDENT_PROGRESS_QUERY);
  const { t } = useTranslation('Reports');
  const [characterOrder, setCharacterOrder] = useState(WdtCharacterOrder.natural);
  const [characterGroup, setCharacterGroup] = useState<number>();

  const reportData: WdtCharacterProgressData[] =
    data?.wdtStudentProgressReport?.characterProgress ?? [];

  const characterGroups: WdtCharacterGroup[] = data?.wdtStudentProgressReport?.characterGroups;

  const filterCharacterGroup = (item: WdtCharacterProgressData): boolean =>
    !characterGroup || item.character?.characterGroupIds?.includes(characterGroup);

  const { characters, levelSeries } = reportData.filter(filterCharacterGroup).reduce(
    (acc, { character, level }) => {
      if (!character) return acc;

      acc.characters.push(character.character);
      acc.levelSeries.push(level ?? 0);
      return acc;
    },
    {
      characters: [] as string[],
      levelSeries: [] as number[]
    }
  );

  const series = [{ name: 'Stars', data: levelSeries }];

  const options = merge({}, BAR_CHART_COMMON_OPTIONS, {
    colors: MODULE_COLOR_MAP[module],
    yaxis: {
      min: 0,
      max: 3,
      title: { text: 'No. of Stars' }
    },
    xaxis: { categories: characters },
    plotOptions: { bar: { columnWidth: `${levelSeries.length * 1.5}%` } }
  });

  useEffect(() => {
    fetchReport({
      variables: merge(getQueryVariables({ entityId, entityType, dateRange }), {
        where: {
          module,
          characterOrder
        }
      })
    });
  }, [fetchReport, entityId, entityType, dateRange, module, characterOrder]);

  const cardTitle = (
    <Box
      display="grid"
      gridAutoFlow="column"
      justifyContent="flex-start"
      alignItems="center"
      gridGap="1rem"
      mb={2}
    >
      <Typography variant="h4">{t(`Progress for ${t(module, { ns: 'ModuleType' })}`)}</Typography>
      {module !== ModuleType.NUMBERS && (
        <CharacterOrderAndFilter
          characterGroups={characterGroups}
          order={characterOrder}
          groupId={characterGroup}
          setOrder={setCharacterOrder}
          setGroupId={setCharacterGroup}
        />
      )}
    </Box>
  );

  return (
    <ReportCard title={cardTitle} loading={loading} reportData={reportData}>
      <BarChart height={250} width={fullChartWidth} series={series} options={options} />
    </ReportCard>
  );
};

export default ProgressForCharactersChartView;
